import React, { useState, useEffect } from "react"
import { useParams, useHistory, Redirect } from "react-router-dom"
import { view } from "@risingstack/react-easy-state"
import styled from "styled-components"
import moment from "moment"
import { PlusOutlined } from "@ant-design/icons"
import { Button, Input, message, Row, Col, Select, DatePicker } from "antd"
import { isContainsBlankCriteria } from "utils/criteria"

import StyledBox from "components/common/styled-components/StyledBox"
import ConditionRow from "components/ConditionRow"
import globalStore from "store/index"

import OrganizationsService from "services/users/organizations.service"
import MinimumWagesService from "services/users/minimum-wages.service"

const StyledConfigurationFormBox = styled.div`
  & .input-box {
    margin: 10px 0px;
  }
  & .action-buttons.submit {
    margin-top: 20px;
  }
`

const blankCondition = {
  field: null,
  operator: null,
  values: null,
}

const MinimumWageEditPage = () => {
  const history = useHistory()
  // const pageState = globalStore.ui.configuration.minimumWages.edit
  const { minimumWageId } = useParams()
  const [criteria, setCriteria] = useState([[blankCondition]])
  const [createButtonLoading, setCreateButtonLoading] = useState(false)
  const [inputValues, setInputValues] = useState({})

  useEffect(() => {
    const fetchMinimumWage = async () => {
      const pageState = globalStore.ui.configuration.minimumWages.edit

      await MinimumWagesService.show({ pageState, minimumWageId })

      const { serverStatus, minimum_wage, minimumWageErrors } = pageState

      if (serverStatus.status !== 200) {
        message.error("Oops!! something went wrong")
        return
      }

      if (minimumWageErrors) {
        message.error(minimumWageErrors[0] || "Coundn't fetch minimum wage")
        return
      }

      const {
        currency,
        frequency,
        amount,
        criteria,
        live_start_at,
        live_end_at,
      } = minimum_wage

      const newCriteria = criteria?.length > 0 ? criteria : [[blankCondition]]

      setCriteria(newCriteria)
      setInputValues({
        currency,
        frequency,
        amount,
        live_start_at: live_start_at ? moment(live_start_at) : null,
        live_end_at: live_end_at ? moment(live_end_at) : null,
      })
    }

    fetchMinimumWage()
  }, [minimumWageId])

  useEffect(() => {
    const pageState = globalStore.currentOrganization
    const organizationFields = pageState.organization?.all_fields_with_datatype
    if (organizationFields && organizationFields.length !== 0) return

    const fetchAllFieldsData = async () => {
      await OrganizationsService.me({ pageState })

      const { organization } = pageState

      if (!organization?.all_fields_with_datatype) {
        message.error("Organization fields not found")
        return
      }
    }

    fetchAllFieldsData()
  }, [])

  const handleInputChange = (value, inputType) => {
    const newInputValues = { ...inputValues, [inputType]: value }

    setInputValues(newInputValues)
  }

  const handleAddNewCondition = (conditionSetIndex, parentConditionIndex) => {
    setCriteria((prevCriteria) => {
      const newCriteria = prevCriteria.map((conditionSet, conSetIndex) => {
        if (conSetIndex !== conditionSetIndex) return conditionSet
        const newConditionSet = [...conditionSet]
        newConditionSet.splice(parentConditionIndex + 1, 0, blankCondition)
        return newConditionSet
      })

      return newCriteria
    })
  }

  const handleRemoveCondition = (conditionSetIndex, deletedConditionIndex) => {
    setCriteria((prevCriteria) => {
      const newCriteria = prevCriteria.map((conditionSet, conSetIndex) => {
        if (conSetIndex !== conditionSetIndex) return conditionSet
        const newConditionSet = [...conditionSet]
        newConditionSet.splice(deletedConditionIndex, 1)
        return newConditionSet
      })

      return newCriteria
    })
  }

  const handleUpdateConditionValues = ({
    conditionSetIndex,
    conditionIndex,
    condition,
  }) => {
    if (!condition) return
    if (condition.operator !== "not" && !condition.values) return

    setCriteria((prevCriteria) => {
      const newCriteria = prevCriteria.map((conditionSet, conSetIndex) => {
        if (conSetIndex !== conditionSetIndex) return conditionSet
        const newConditionSet = [...conditionSet]
        return newConditionSet.map((c, index) => {
          if (index !== conditionIndex) return c

          return condition
        })
      })

      return newCriteria
    })
  }

  const handleUpdate = async () => {
    // if (!inputValues["name"]) {
    //   message.error("Name can't be blank")
    //   return
    // }
    if (!inputValues["currency"]) {
      message.error("Currency can't be blank")
      return
    }
    if (!inputValues["amount"]) {
      message.error("Amount can't be blank")
      return
    }
    if (!inputValues["frequency"]) {
      message.error("Frequency can't be blank")
      return
    }

    if (!inputValues["live_start_at"]) {
      message.error("Live start at can't be blank")
      return
    }

    if (!inputValues["live_end_at"]) {
      message.error("Live end at can't be blank")
      return
    }

    if (isContainsBlankCriteria(criteria)) {
      message.error("Criteria cannot be blank")
      return
    }

    const pageState = {}
    const postData = {
      minimum_wage: {
        criteria: criteria.filter((c) => c.length > 0),
        ...inputValues,
      },
    }

    setCreateButtonLoading(true)
    await MinimumWagesService.update({
      pageState,
      values: postData,
      minimumWageId,
    })
    setCreateButtonLoading(false)

    const { serverStatus, minimumWageErrors } = pageState

    if (minimumWageErrors) {
      message.error(minimumWageErrors[0] || "Failed to update minimum wage")
      return
    }

    if (serverStatus.status === 200) {
      message.success("Minimum Wage updated.")
      history.push(`/configuration/minimum-wages/${minimumWageId}`)
      setCriteria([[blankCondition]])
      setInputValues({})

      return
    }

    message.error("Oops!! something went wrong")
  }

  const renderPageContent = () => {
    return (
      <StyledConfigurationFormBox>
        <h1>Minimum Wage</h1>
        <Row gutter={20}>
          <Col span={8}>
            <div className="input-box">
              <strong>Currency</strong>
              <Input
                value={inputValues["currency"]}
                onChange={(e) => handleInputChange(e.target.value, "currency")}
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="input-box">
              <strong>Amount</strong>
              <Input
                value={inputValues["amount"]}
                onChange={(e) => handleInputChange(e.target.value, "amount")}
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="input-box">
              <strong>Frequency</strong>
              <Select
                style={{ display: "block" }}
                value={inputValues["frequency"]}
                onChange={(value) => handleInputChange(value, "frequency")}
              >
                <Select.Option value="daily">Daily</Select.Option>
                <Select.Option value="weekly">Weekly</Select.Option>
                <Select.Option value="monthly">Monthly</Select.Option>
                <Select.Option value="quarterly">Quarterly</Select.Option>
                <Select.Option value="half_yearly">Half Yearly</Select.Option>
                <Select.Option value="annually">Annually</Select.Option>
              </Select>
              {/* <Input
                value={inputValues["frequency"]}
                onChange={(e) => handleInputChange(e, "frequency")}
              /> */}
            </div>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={12}>
            <div className="input-box">
              <strong>Live Start At</strong>
              <div>
                <DatePicker
                  value={inputValues["live_start_at"]}
                  style={{ width: "100%" }}
                  onChange={(dateMonent, dateString) =>
                    handleInputChange(dateMonent, "live_start_at")
                  }
                />
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="input-box">
              <strong>Live End At</strong>
              <div>
                <DatePicker
                  value={inputValues["live_end_at"]}
                  style={{ width: "100%" }}
                  onChange={(dateMonent, dateString) =>
                    handleInputChange(dateMonent, "live_end_at")
                  }
                />
              </div>
            </div>
          </Col>
        </Row>
        <div className="input-box">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <strong>Criteria</strong>
            {criteria.filter((c) => c.length > 0).length === 0 && (
              <Button
                shape="circle"
                onClick={() => setCriteria([[blankCondition]])}
              >
                <PlusOutlined />
              </Button>
            )}
          </div>
          {criteria.map((conditionSet, conditionSetIndex) => {
            return (
              <div key={conditionSetIndex}>
                {conditionSet.map((condition, conditionIndex) => {
                  return (
                    <ConditionRow
                      condition={condition}
                      key={conditionIndex}
                      conditionIndex={conditionSetIndex}
                      lastInputGroup={
                        conditionSet.length === conditionIndex + 1
                      }
                      conditionSetLength={conditionSet.length}
                      firstInputGroup={conditionIndex === 0}
                      inputGroupIndex={conditionIndex}
                      updateConditionValues={(condition) =>
                        handleUpdateConditionValues({
                          conditionSetIndex,
                          conditionIndex,
                          condition,
                        })
                      }
                      addNewCondition={() =>
                        handleAddNewCondition(conditionSetIndex, conditionIndex)
                      }
                      removeCondition={() =>
                        handleRemoveCondition(conditionSetIndex, conditionIndex)
                      }
                      organizationFields={
                        globalStore.currentOrganization.all_fields_with_datatype
                      }
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
        {/* {<div className="input-box">
          <strong>Criteria</strong>
          {criteria.map((c, index) => (
            <ConditionRow
              key={index}
              criteria={c}
              lastInputGroup={criteria.length === index + 1}
              firstInputGroup={index === 0}
              inputGroupIndex={index}
              updateCriteriaValues={(values) =>
                handleUpdateCriteriaValues(values, index)
              }
              addNewCriteria={handleAddNewCriteria}
              removeCriteria={() => handleRemoveCondition(index)}
            />
          ))}
        </div>} */}
        <div className="action-buttons submit">
          <Button
            type="primary"
            loading={createButtonLoading}
            onClick={handleUpdate}
          >
            Save
          </Button>
        </div>
      </StyledConfigurationFormBox>
    )
  }

  const accessibleFeatures = globalStore.currentUser?.accessible_features || []

  if (
    accessibleFeatures?.length > 0 &&
    !accessibleFeatures.includes("fitment-data-edit")
  )
    return <Redirect to={`/configuration/minimum-wages/${minimumWageId}`} />

  return (
    <div className="page-content dashboard-page">
      <StyledBox>{renderPageContent()}</StyledBox>
    </div>
  )
}

export default view(MinimumWageEditPage)
